import React, { lazy, Suspense, useContext, useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import {
  AppBar,
  IconButton,
  Toolbar,
  CircularProgress,
  Box,
  Fade,
  Zoom,
  Fab,
  Typography,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  Settings,
  Logout,
} from "@mui/icons-material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "@mui/material/styles";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AuthGuard, { UserContext } from "./components/AuthGuard";
import GeneralErrorPage from "./components/GeneralErrorPage";
import { ErrorBoundary } from "react-error-boundary";
import RoleGuard from "./components/guards/RoleGuard";
import { LoggedInRouter } from "./LoggedInRouter";
import Posts from "./components/Posts";
import PostEditor from "./components/PostEditor";
import Homepage from "./components/Homepage";
import AdminUser from "./components/admin/AdminUser";
import AdminLog from "./components/admin/AdminLog";
import AdminEmail from "./components/admin/AdminEmail";

/**
 * Lazy load components to improve performance and reduce initial bundle size
 */
const SignInScreen = lazy(() => import("./components/SignInScreen"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));

const generalRoutes = [
  {
    path: "/",
    element: <Homepage />,
  },
  {
    path: "/login",
    element: <SignInScreen />,
  },
  {
    path: "/posts",
    element: (
      <RoleGuard roles={["admin", "user"]}>
        <Posts />
      </RoleGuard>
    ),
  },
  {
    path: "/posts/new",
    element: (
      <RoleGuard roles={["admin", "user"]}>
        <PostEditor />
      </RoleGuard>
    ),
  },
  {
    path: "/posts/edit/:id",
    element: (
      <RoleGuard roles={["admin", "user"]}>
        <PostEditor />
      </RoleGuard>
    ),
  },
  {
    path: "/admin/users",
    element: (
      <RoleGuard roles={["admin"]}>
        <AdminUser />
      </RoleGuard>
    ),
  },
  {
    path: "/admin/email",
    element: (
      <RoleGuard roles={["admin"]}>
        <AdminEmail />
      </RoleGuard>
    ),
  },
  {
    path: "/admin/log",
    element: (
      <RoleGuard roles={["admin"]}>
        <AdminLog />
      </RoleGuard>
    ),
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

const StyledFab = styled(Fab)({
  position: "fixed",
  bottom: 16,
  right: 16,
});

const LoadingScreen = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const RenderUnauthScreens = () => {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const checkScrollTop = () => {
      const scrolled = window.pageYOffset > 400;
      setShowButton(scrolled);
    };

    window.addEventListener("scroll", checkScrollTop);
    return () => window.removeEventListener("scroll", checkScrollTop);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth={false} sx={{ padding: 0, margin: 0 }}>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {generalRoutes.map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
        </Routes>
      </Suspense>
      <Fade in={showButton}>
        <Zoom in={showButton} timeout={1000} unmountOnExit>
          <StyledFab
            onClick={scrollToTop}
            color="secondary"
            size="small"
            aria-label="scroll back to top"
          >
            <KeyboardArrowUpIcon />
          </StyledFab>
        </Zoom>
      </Fade>
    </Container>
  );
};

const Main = () => {
  const [user] = useContext(UserContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await firebase.auth().signOut();
      // The AuthGuard will automatically handle the state change
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (!user) {
    return <RenderUnauthScreens />;
  }

  return (
    <Box>
      <AppBar position="fixed">
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Counter.Quest
          </Typography>
          
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <Settings sx={{ mr: 1 }} />
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Suspense fallback={<LoadingScreen />}>
          <LoggedInRouter />
        </Suspense>
      </Box>
    </Box>
  );
};

// Main App component that wraps everything with BrowserRouter and AuthGuard
const App = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary FallbackComponent={GeneralErrorPage}>
        <AuthGuard>
          <Main />
        </AuthGuard>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
